import React, { useContext, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useMutation } from '@apollo/client';
import { MenuItem, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import * as Sentry from '@sentry/react';

import { UserContext } from '@/components/App';
import NavResponsiveModal, { ModalHeader } from '@/components/Navigation/NavResponsiveModal';
import { ModalContent, GreyBody, PrimaryButton, SpacerV1 } from '@/styles/blocks';

import PricingRow from './PricingRow';
import { SUPPLY_CHAIN_LEG_LABELS } from './constants';
import { GET_CORP_PRICING_OVERRIDES, INSERT_CORP_PRICING_OVERRIDE } from '../graphql';
import { CorpAdminContext } from '../store';
import { connectionPartnerships, MARKETPLACE_KEY } from '../utils';

const PricingModal = ({
    open,
    onClose,
    carrierPricing = [],
    shipperPricing = [],
    defaultPricing = [],
    defaultLegs = [],
    carrier_id,
    carrier,
    carrierLegs,
    shipper,
    shipper_id,
    shipperName,
    forContractor = false,
}) => {
    const { refetchAdminClients } = useContext(UserContext);
    const { state, callbacks } = useContext(CorpAdminContext);
    const { orgShipperIds, organizationId } = state;
    const { setNotification } = callbacks;
    // A default covers every shipper, so only org-level tariffs fit it.
    const orgTariffs = state.tariffs.filter((tariff) => !tariff.client_id);

    const isMarketplace = carrier_id === MARKETPLACE_KEY;
    // For a real carrier, restrict legs to what the carrier supports. Marketplace and
    // legacy carriers without supply_chain_legs fall back to the full enum.
    // E2E supersedes FINAL_MILE — if a carrier has E2E, FINAL_MILE is dropped from options.
    const availableLegs = useMemo(() => {
        let base = isMarketplace ? ['FINAL_MILE', 'MIDDLE_MILE'] : ['FINAL_MILE'];
        if (carrierLegs?.length > 0) base = carrierLegs;
        return base.includes('E2E') ? base.filter((l) => l !== 'FINAL_MILE') : base;
    }, [isMarketplace, carrierLegs]);

    // One default per leg, carrier and org: a leg already set is edited in its row below.
    const unsetDefaultLegs = defaultLegs.filter((value) => !defaultPricing.some((p) => p.override_type === value));

    let initialSide = 'carrier';
    if (isMarketplace) initialSide = 'shipper';
    if (!shipper_id) initialSide = 'default';

    const [side, setSide] = useState(initialSide);
    const [pickedLeg, setLeg] = useState('');
    const [defaultTariffId, setDefaultTariffId] = useState('');
    const legOptions = side === 'default' ? unsetDefaultLegs : availableLegs;
    const leg = legOptions.includes(pickedLeg) ? pickedLeg : legOptions[0] || '';

    const [insertOverride, { loading: inserting }] = useMutation(INSERT_CORP_PRICING_OVERRIDE, {
        update(cache, { data }) {
            const newOverride = data?.inserted;
            if (!newOverride) return;

            const existing = cache.readQuery({
                query: GET_CORP_PRICING_OVERRIDES,
                variables: { orgShipperIds, organization_id: organizationId },
            });
            if (!existing) return;

            cache.writeQuery({
                query: GET_CORP_PRICING_OVERRIDES,
                variables: { orgShipperIds, organization_id: organizationId },
                data: { pricing_overrides: [...existing.pricing_overrides, newOverride] },
            });
        },
    });

    const defaultTariff = orgTariffs.find((tariff) => tariff.tariff_id === defaultTariffId);

    const handleAddDefault = async () => {
        const object = {
            enabled: true,
            oms: false,
            client_id: carrier_id,
            partner_client_id: null,
            organization_id: organizationId,
            marketplace_tier: 'T1_INTEGRATIONS_ONLY',
            override_type: leg,
            tariff_id: defaultTariff.tariff_id,
            algo_type: defaultTariff.algo_type,
        };
        try {
            await insertOverride({ variables: { object } });
            setDefaultTariffId('');
            setNotification?.({ severity: 'success', message: 'Default pricing added' });
        } catch (err) {
            console.error('Default pricing insert failed', err?.response?.data || err);
            Sentry.captureException(err);
            setNotification?.({ severity: 'error', message: 'Failed to add default pricing' });
        }
    };

    const handleAdd = async () => {
        const isCarrierSide = side === 'carrier';
        // Carrier-side: client_id=carrier, partner_client_id=shipper.
        // Shipper-side: client_id=shipper, partner_client_id=carrier (or null on marketplace).
        let partnerClientId = isCarrierSide ? shipper_id : carrier_id;
        if (isMarketplace && !isCarrierSide) partnerClientId = null;
        const object = {
            enabled: true,
            oms: false,
            client_id: isCarrierSide ? carrier_id : shipper_id,
            partner_client_id: partnerClientId,
            marketplace_tier: isMarketplace ? 'T3_FULLY_MANAGED' : 'T1_INTEGRATIONS_ONLY',
            override_type: leg,
        };
        try {
            await insertOverride({
                variables: {
                    object,
                    partnerships: connectionPartnerships({ shipper, carrier: isMarketplace ? null : carrier }),
                },
            });
            refetchAdminClients?.();
            setNotification?.({ severity: 'success', message: 'Pricing override added' });
        } catch (err) {
            console.error('Pricing override insert failed', err?.response?.data || err);
            Sentry.captureException(err);
            setNotification?.({ severity: 'error', message: 'Failed to add pricing override' });
        }
    };

    const hasCarrier = carrierPricing.length > 0;
    const hasShipper = shipperPricing.length > 0;
    const hasDefault = defaultPricing.length > 0;
    const canAddCarrier = !isMarketplace && !!carrier_id;
    const isForShipper = !!shipper_id;
    const isDefaultSide = side === 'default';
    // A default without a tariff outranks the marketplace rate and blanks carrier pay.
    const missingDefaultTariff = isDefaultSide && !defaultTariff;
    const addDisabled = inserting || !leg || missingDefaultTariff || (!isDefaultSide && !isForShipper);
    const canAddCarrierForShipper = canAddCarrier && isForShipper;
    const lacksDefault = !isForShipper && !hasDefault;
    const lacksShipperPricing = isForShipper && !hasCarrier && !hasShipper;
    // A contractor is paid against a Flex Center (shipper_id here), which pays nothing back.
    const canAddShipper = isForShipper && !forContractor;
    const partnerNoun = forContractor ? 'Flex Center' : 'Shipper';

    let title = 'Default Pricing';
    if (isForShipper) title = shipperName ? `Pricing — ${shipperName}` : 'Pricing';

    return (
        <NavResponsiveModal open={open} onClose={onClose} maxWidth="md" fullWidth>
            <ModalHeader
                title={title}
                onClose={onClose}
                css={css`
                    border-bottom: 1px solid lightgray;
                `}
            />
            <ModalContent
                css={css`
                    width: 100%;
                `}
            >
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        margin-bottom: 1rem;
                    `}
                >
                    <TextField
                        select
                        size="small"
                        variant="outlined"
                        label="Add"
                        value={side}
                        onChange={(e) => setSide(e.target.value)}
                        css={css`
                            min-width: 200px;
                        `}
                    >
                        {canAddCarrier && <MenuItem value="default">Default Carrier Pricing</MenuItem>}
                        {canAddCarrierForShipper && <MenuItem value="carrier">Carrier Pricing</MenuItem>}
                        {canAddShipper && <MenuItem value="shipper">Shipper Pricing</MenuItem>}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        variant="outlined"
                        label="Leg"
                        value={leg}
                        onChange={(e) => setLeg(e.target.value)}
                        disabled={legOptions.length === 0}
                        css={css`
                            min-width: 160px;
                        `}
                    >
                        {legOptions.map((value) => (
                            <MenuItem key={value} value={value}>
                                {SUPPLY_CHAIN_LEG_LABELS[value] || value}
                            </MenuItem>
                        ))}
                    </TextField>
                    {isDefaultSide && (
                        <Autocomplete
                            id="default-pricing-tariff"
                            options={orgTariffs}
                            value={defaultTariff || null}
                            onChange={(e, tariff) => setDefaultTariffId(tariff?.tariff_id ?? '')}
                            getOptionLabel={(tariff) => tariff.name || 'Tariff'}
                            getOptionSelected={(option, value) => option.tariff_id === value.tariff_id}
                            disabled={orgTariffs.length === 0}
                            renderInput={(params) => (
                                <TextField {...params} label="Tariff" size="small" variant="outlined" />
                            )}
                            css={css`
                                min-width: 240px;
                            `}
                        />
                    )}
                    <PrimaryButton onClick={isDefaultSide ? handleAddDefault : handleAdd} disabled={addDisabled}>
                        Add
                    </PrimaryButton>
                </div>

                {hasDefault && (
                    <>
                        <GreyBody
                            css={css`
                                font-weight: 600;
                            `}
                        >
                            Default for All {partnerNoun}s
                        </GreyBody>
                        <SpacerV1 />
                        {defaultPricing.map((pricing) => (
                            <PricingRow key={pricing.pricing_override_id} pricing={pricing} side="carrier" />
                        ))}
                    </>
                )}

                {lacksDefault && <GreyBody>No default tariff yet.</GreyBody>}

                {lacksShipperPricing && (
                    <GreyBody>No pricing overrides configured for this {partnerNoun.toLowerCase()}.</GreyBody>
                )}

                {hasCarrier && (
                    <>
                        <GreyBody
                            css={css`
                                font-weight: 600;
                                margin-top: ${hasDefault ? '1rem' : '0'};
                            `}
                        >
                            Carrier Pricing
                        </GreyBody>
                        <SpacerV1 />
                        {carrierPricing.map((pricing) => (
                            <PricingRow
                                key={pricing.pricing_override_id}
                                pricing={pricing}
                                shipper_id={shipper_id}
                                side="carrier"
                                forContractor={forContractor}
                            />
                        ))}
                    </>
                )}

                {hasShipper && (
                    <>
                        <GreyBody
                            css={css`
                                font-weight: 600;
                                margin-top: ${hasCarrier || hasDefault ? '1rem' : '0'};
                            `}
                        >
                            Shipper Pricing
                        </GreyBody>
                        <SpacerV1 />
                        {shipperPricing.map((pricing) => (
                            <PricingRow
                                key={pricing.pricing_override_id}
                                pricing={pricing}
                                shipper_id={shipper_id}
                                side="shipper"
                            />
                        ))}
                    </>
                )}
            </ModalContent>
        </NavResponsiveModal>
    );
};

export default PricingModal;
